import { Spinner } from '@autoaff/ui';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { createRootRoute, createRoute, createRouter, Navigate, RouterProvider } from '@tanstack/react-router';
import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
// PWA: ловит установку, связь и обновление версии с первой секунды загрузки
import './pwa';
// Видимая часть экрана на телефоне (клавиатура закрывает низ страницы)
import './viewport';
import { api } from './api';
import { Login } from './Login';
import { AdsPage } from './pages/Ads';
import { AgentLayout, ChatsPage } from './pages/Agent';
import { ChannelTab } from './pages/agent/Channel';
import { FeedTab } from './pages/agent/Feed';
import { JournalTab } from './pages/agent/Journal';
import { KnowledgeTab } from './pages/agent/Knowledge';
import { ScheduleTab } from './pages/agent/Schedule';
import { TasksTab } from './pages/agent/Tasks';
import { BrowserScreenPage } from './pages/BrowserScreen';
import { ComparePage } from './pages/creatives/Compare';
import { CreativePage } from './pages/creatives/CreativePage';
import { LibraryPage } from './pages/creatives/Library';
import { NetworkPage } from './pages/creatives/Network';
import { CreativesPage } from './pages/creatives/Pool';
import { WorksPage } from './pages/creatives/Works';
import { DecisionsPage } from './pages/Decisions';
import { DevPage } from './pages/Dev';
import { DevCrPage } from './pages/DevCr';
import { FinancePage } from './pages/Finance';
import { LandingsPage } from './pages/Landings';
import { LeadsPage } from './pages/Leads';
import { MorePage } from './pages/More';
import { OffersPage } from './pages/Offers';
import { SettingsPage } from './pages/Settings';
import { SummaryPage } from './pages/Summary';
import { isSettingsTab, type SettingsTab } from './pages/settings-nav';
import { Shell } from './shell/Shell';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 10_000, retry: 1, refetchOnWindowFocus: true } },
});

interface AuthState {
  registered: boolean;
  authenticated: boolean;
  devBypass: boolean;
  password?: boolean;
  passkey?: boolean;
}

function AuthGate() {
  const q = useQuery({ queryKey: ['auth'], queryFn: () => api<AuthState>('/api/auth/state') });
  useEffect(() => {
    const on = () => void queryClient.invalidateQueries({ queryKey: ['auth'] });
    window.addEventListener('aaff:unauthorized', on);
    return () => window.removeEventListener('aaff:unauthorized', on);
  }, []);
  if (q.isPending) {
    return (
      <div className="auth">
        <Spinner />
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="auth">
        <div className="card auth-card">
          <h1>Нет связи с платформой</h1>
          <p>Сервер не отвечает. Проверьте интернет и попробуйте ещё раз.</p>
          <button type="button" className="btn btn-primary" onClick={() => q.refetch()}>
            Повторить
          </button>
        </div>
      </div>
    );
  }
  if (!q.data.authenticated) {
    return (
      <Login
        registered={q.data.registered}
        // Старый api без этих полей — значит, вход только ключом
        methods={{ password: q.data.password ?? false, passkey: q.data.passkey ?? q.data.registered }}
        onDone={() => void queryClient.invalidateQueries({ queryKey: ['auth'] })}
      />
    );
  }
  return <Shell />;
}

const root = createRootRoute({ component: AuthGate });
const r = <P extends string>(path: P, component: () => React.ReactNode) =>
  createRoute({ getParentRoute: () => root, path, component });

const agent = createRoute({ getParentRoute: () => root, path: '/agent', component: AgentLayout });
const agentIndex = createRoute({
  getParentRoute: () => agent,
  path: '/',
  component: () => <Navigate to="/agent/feed" replace />,
});
const agentChats = createRoute({ getParentRoute: () => agent, path: '/chats', component: ChatsPage });
const agentChat = createRoute({
  getParentRoute: () => agent,
  path: '/chats/$threadId',
  component: ChatsPage,
});
const agentTabs = [
  createRoute({ getParentRoute: () => agent, path: '/feed', component: FeedTab }),
  createRoute({ getParentRoute: () => agent, path: '/journal', component: JournalTab }),
  createRoute({ getParentRoute: () => agent, path: '/tasks', component: TasksTab }),
  createRoute({ getParentRoute: () => agent, path: '/schedule', component: ScheduleTab }),
  createRoute({ getParentRoute: () => agent, path: '/knowledge', component: KnowledgeTab }),
  createRoute({ getParentRoute: () => agent, path: '/channel', component: ChannelTab }),
];

const leads = createRoute({
  getParentRoute: () => root,
  path: '/leads',
  component: LeadsPage,
  // ?lead=123 — открытая карточка лида: ссылкой можно поделиться, «назад» её закрывает
  validateSearch: (s: Record<string, unknown>): { lead?: number; show?: 'risky' } => {
    const n = Number(s.lead);
    return {
      ...(Number.isInteger(n) && n > 0 ? { lead: n } : {}),
      ...(s.show === 'risky' ? { show: 'risky' as const } : {}),
    };
  },
});

const creativesRoute = createRoute({
  getParentRoute: () => root,
  path: '/creatives',
  component: CreativesPage,
  // ?batch=12 — ролики одного батча
  validateSearch: (s: Record<string, unknown>): { batch?: number } => {
    const n = Number(s.batch);
    return Number.isInteger(n) && n > 0 ? { batch: n } : {};
  },
});
const creativeRoute = createRoute({
  getParentRoute: () => root,
  path: '/creatives/$id',
  component: CreativePage,
});

// ?tab=integrations — вкладка настроек; без неё вкладку выбирает якорь (#claude-access) или открывается первая
const settingsRoute = createRoute({
  getParentRoute: () => root,
  path: '/settings',
  component: SettingsPage,
  validateSearch: (s: Record<string, unknown>): { tab?: SettingsTab } =>
    isSettingsTab(s.tab) ? { tab: s.tab } : {},
});

// ?site=instagram — открыть страницу входа, как только экран подключится
const browserScreen = createRoute({
  getParentRoute: () => root,
  path: '/settings/browser',
  component: BrowserScreenPage,
  validateSearch: (s: Record<string, unknown>): { site?: string } =>
    typeof s.site === 'string' && /^[a-z]{2,20}$/.test(s.site) ? { site: s.site } : {},
});

const routeTree = root.addChildren([
  r('/', SummaryPage),
  r('/decisions', DecisionsPage),
  agent.addChildren([agentIndex, agentChats, agentChat, ...agentTabs]),
  r('/ads', AdsPage),
  r('/offers', OffersPage),
  r('/landings', LandingsPage),
  creativesRoute,
  r('/creatives/library', LibraryPage),
  r('/creatives/network', NetworkPage),
  r('/creatives/works', WorksPage),
  r('/creatives/compare/$id', ComparePage),
  creativeRoute,
  leads,
  r('/finance', FinancePage),
  settingsRoute,
  r('/dev', DevPage),
  r('/dev/$id', DevCrPage),
  browserScreen,
  r('/more', MorePage),
]);

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultNotFoundComponent: () => <Navigate to="/" replace />,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const el = document.getElementById('root');
if (el) {
  createRoot(el).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
}
